import React from 'react'
import cn from 'classnames'
import OrdinalsIcon from 'components/shared/OrdinalsIcon'
import ResolvedOrdinalRender from 'components/answers/FileEmbed/ResolvedOrdinalRender'
import styles from './ordinal-details.module.scss'
import type { CombinedOrdinal, OrdinalPreview } from './OrdinalsViewer'
import { Banana, X, Send } from 'lucide-react'
import ShowInProfileButton from './ShowInProfileButton'
import HiddenButton from './HiddenButton'

type Props = {
    selectedOrdinal: OrdinalPreview
    inscriptionOutpoint: string
    onClose: () => void
    onSendClick: (ordinal: CombinedOrdinal) => void
    showShowInProfile?: boolean
    showHidden?: boolean
    showSend?: boolean
}

export default function OrdinalDetails({
    selectedOrdinal,
    inscriptionOutpoint,
    onClose,
    onSendClick,
    showShowInProfile = true,
    showHidden = true,
    showSend = true,
}: Props) {
    const ordinal = selectedOrdinal.ordinal
    const fileType = ordinal.origin?.data?.insc?.file?.type || ''
    const utxoOutpoint = ordinal.utxo_outpoint
    const ordinalId = ordinal.treechatData?.id || ordinal.descriptor?.bsv_ordinal_id || null
    const showInProfile =
        !!ordinal.users_ordinal?.show_in_profile ||
        !!ordinal.treechatData?.users_ordinal?.show_in_profile
    const isHidden =
        !!ordinal.users_ordinal?.hidden || !!ordinal.treechatData?.users_ordinal?.hidden
    const canSend = showSend && !!ordinal.treechatData?.id && ordinal.status === 'confirmed'

    const metadata: Array<{ label: string; value: string }> = [
        { label: 'Inscription Outpoint', value: String(inscriptionOutpoint || '') },
        ...(utxoOutpoint && utxoOutpoint !== inscriptionOutpoint
            ? [{ label: 'UTXO Outpoint', value: String(utxoOutpoint) }]
            : []),
        { label: 'Transaction ID', value: String(ordinal.txid || '') },
        {
            label: 'Status',
            value:
                ordinal.status === 'unconfirmed'
                    ? 'Pending'
                    : ordinal.status === 'pending'
                    ? 'Pending'
                    : 'Confirmed',
        },
    ]

    if (ordinal.origin?.data?.map?.name)
        metadata.push({ label: 'Name', value: ordinal.origin.data.map.name })
    if (ordinal.origin?.data?.insc?.file?.type)
        metadata.push({ label: 'File Type', value: ordinal.origin.data.insc.file.type })
    if (ordinal.origin?.data?.insc?.file?.size)
        metadata.push({
            label: 'File Size',
            value: `${(ordinal.origin.data.insc.file.size / 1024).toFixed(2)} KB`,
        })

    const open = (url: string) => window.open(url, '_blank')

    return (
        <div className={styles.lightboxContent} onClick={e => e.stopPropagation()}>
            <button className={styles.closeButton} onClick={onClose} aria-label="Close">
                <X size={20} />
            </button>

            <div className={styles.previewSection} data-no-modal-interaction>
                <ResolvedOrdinalRender
                    treechatOrdinalId={ordinal.treechatData?.id}
                    treechatPreviewUrl={ordinal.treechatData?.preview_url}
                    seenOutpoint={ordinal.utxo_outpoint || ordinal.outpoint}
                    originOutpointHint={inscriptionOutpoint}
                    assumeOrigin={!!ordinal.treechatData}
                    fileType={fileType}
                    className={styles.mainView}
                    allowInteraction={true}
                    forceLive={!!selectedOrdinal.forceLive}
                />
            </div>

            <div className={styles.metadataSection}>
                <div className={styles.metadataHeader}>
                    <span>Ordinal Details</span>
                    <div className={cn(styles.headerActions, styles.mobileOnly)}>
                        {showHidden && (
                            <HiddenButton
                                ordinalId={ordinalId}
                                initialValue={isHidden}
                                className={styles.headerAction}
                                activeClassName={styles.headerActionHiddenActive}
                                animateClassName={styles.headerActionPulse}
                                showLabel={false}
                                size={16}
                            />
                        )}
                        {showShowInProfile && (
                            <ShowInProfileButton
                                ordinalId={ordinalId}
                                initialValue={showInProfile}
                                className={styles.headerAction}
                                activeClassName={styles.headerActionActive}
                                animateClassName={styles.headerActionPulse}
                                showLabel={false}
                                size={16}
                            />
                        )}
                        {canSend && (
                            <button
                                className={cn(styles.headerAction, styles.sendAction)}
                                onClick={() => onSendClick(ordinal)}
                                title="Send Ordinal"
                                data-testid="ordinal-send-open-button"
                            >
                                <Send size={16} />
                            </button>
                        )}
                    </div>
                </div>

                <div className={styles.sidebarBody}>
                    <div className={styles.metadataContent}>
                        {metadata.map((item, index) => (
                            <div key={index} className={styles.metadataItem}>
                                <label>{item.label}</label>
                                <div className={styles.value}>{item.value}</div>
                            </div>
                        ))}
                    </div>

                    {/* Mobile: keep buttonContainer inside the scroll container */}
                    <div className={cn(styles.buttonContainer, styles.mobileOnly)}>
                        <div className={styles.viewSection}>
                            <div className={styles.sectionLabel}>View in</div>
                            <div className={styles.viewButtons}>
                                <button
                                    className={styles.viewButton}
                                    onClick={() =>
                                        open(
                                            `https://alpha.1satordinals.com/outpoint/${inscriptionOutpoint}/timeline`,
                                        )
                                    }
                                    title="1Sat Ordinals"
                                >
                                    <OrdinalsIcon size={18} />
                                </button>

                                <button
                                    className={styles.viewButton}
                                    onClick={() =>
                                        open(
                                            `https://zoide.io/market/inscription/${inscriptionOutpoint}`,
                                        )
                                    }
                                    title="Zoide"
                                >
                                    <img src="/zoide-dark.png" alt="Zoide" />
                                </button>

                                <button
                                    className={styles.viewButton}
                                    onClick={() =>
                                        open(`https://3dordi.io/ordinal/${inscriptionOutpoint}`)
                                    }
                                    title="3DOrdi"
                                >
                                    <img src="/assets/images/ordi-mascot.png" alt="3DOrdi" />
                                </button>

                                <button
                                    className={styles.viewButton}
                                    onClick={() =>
                                        open(
                                            `https://ordinals.gorillapool.io/content/${inscriptionOutpoint}`,
                                        )
                                    }
                                    title="ordinals.gorillapool.io"
                                >
                                    <Banana size={18} />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Desktop: keep buttonContainer pinned to bottom of sidebar */}
                <div className={cn(styles.buttonContainer, styles.desktopOnly)}>
                    <div className={styles.viewSection}>
                        <div className={styles.sectionLabel}>View in</div>
                        <div className={styles.viewButtons}>
                            <button
                                className={styles.viewButton}
                                onClick={() =>
                                    open(
                                        `https://alpha.1satordinals.com/outpoint/${inscriptionOutpoint}/timeline`,
                                    )
                                }
                                title="1Sat Ordinals"
                            >
                                <OrdinalsIcon size={18} />
                            </button>
                            <button
                                className={styles.viewButton}
                                onClick={() =>
                                    open(
                                        `https://zoide.io/market/inscription/${inscriptionOutpoint}`,
                                    )
                                }
                                title="Zoide"
                            >
                                <img src="/zoide-dark.png" alt="Zoide" />
                            </button>
                            <button
                                className={styles.viewButton}
                                onClick={() =>
                                    open(`https://3dordi.io/ordinal/${inscriptionOutpoint}`)
                                }
                                title="3DOrdi"
                            >
                                <img src="/assets/images/ordi-mascot.png" alt="3DOrdi" />
                            </button>
                            <button
                                className={styles.viewButton}
                                onClick={() =>
                                    open(
                                        `https://ordinals.gorillapool.io/content/${inscriptionOutpoint}`,
                                    )
                                }
                                title="ordinals.gorillapool.io"
                            >
                                <Banana size={18} />
                            </button>
                        </div>
                    </div>

                    <div className={styles.primaryActions}>
                        {canSend && (
                            <button
                                className={styles.sendButtonProminent}
                                onClick={() => onSendClick(ordinal)}
                                data-testid="ordinal-send-open-button"
                            >
                                <Send size={18} />
                                <span>Send Ordinal</span>
                            </button>
                        )}
                        <div className={styles.secondaryActionsRow}>
                            {showHidden && (
                                <HiddenButton
                                    ordinalId={ordinalId}
                                    initialValue={isHidden}
                                    className={styles.subActionButton}
                                    activeClassName={styles.subActionButtonHiddenActive}
                                    animateClassName={styles.subActionButtonPulse}
                                    showLabel={true}
                                    size={16}
                                />
                            )}
                            {showShowInProfile && (
                                <ShowInProfileButton
                                    ordinalId={ordinalId}
                                    initialValue={showInProfile}
                                    className={styles.subActionButton}
                                    activeClassName={styles.subActionButtonActive}
                                    animateClassName={styles.subActionButtonPulse}
                                    showLabel={true}
                                    size={16}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
